import { ParamMap } from '@angular/router';

import { ContractReminderStatus } from '@gi/enums/contract-reminder-status.enum';
import { ContractStatus } from '@gi/enums/contract-status.enum';
import { ContractsQueryParams } from '@gi/enums/contracts-query-params.enum';
import { ContractsFilters } from '@gi/interfaces/contracts-filters';
import { Contract } from '@gi/models/contract';
import { Prestation } from '@gi/models/prestation';
import { FiltersParams, HttpHelper } from '@helpers/http.helper';
import { businessUnitsParam, contributorsParam, statusAtParam } from '@helpers/query-params.helper';
import { TimeTrackingsTask } from '@models/time-trackings-task';

export class ContractsHelper {
  static toParams(filters: ContractsFilters): FiltersParams {
    return {
      status: filters?.status?.length ? filters.status.join('|') : undefined,
      [ContractsQueryParams.businessUnits]: filters?.businessUnits?.length
        ? HttpHelper.idsFilter(filters?.businessUnits)
        : undefined,
      [ContractsQueryParams.owners]: filters?.owners?.[0]?.id ? HttpHelper.idsFilter(filters?.owners) : undefined,
      without_owner: filters?.owners?.[0]?.id === null ? true : undefined,
      archived: typeof filters?.archived === 'boolean' ? filters?.archived : undefined,
      [ContractsQueryParams.statusAt]:
        filters.startDate || filters.endDate ? HttpHelper.dateFilter(filters.startDate, filters.endDate) : undefined,
      [ContractsQueryParams.reminderStatus]: filters?.reminder_status ?? undefined,
      included_prestations: filters?.tasks?.filter((task: TimeTrackingsTask) => task.included)?.length
        ? HttpHelper.idsFilter(filters?.tasks?.filter((task: TimeTrackingsTask) => task.included))
        : undefined,
      excluded_prestations: filters?.tasks?.filter((task: TimeTrackingsTask) => !task.included)?.length
        ? HttpHelper.idsFilter(filters?.tasks?.filter((task: TimeTrackingsTask) => !task.included))
        : undefined,
      loss_reasons: filters?.loss_reasons?.length ? filters.loss_reasons.join('|') : undefined
    };
  }

  static fromParams(params: ParamMap): ContractsFilters {
    const filters: ContractsFilters = {};

    if (params.has(ContractsQueryParams.reminderStatus)) {
      filters.reminder_status = params.get(ContractsQueryParams.reminderStatus) as ContractReminderStatus;
    }

    if (params.has(ContractsQueryParams.statusAt)) {
      const { startDate, endDate } = statusAtParam(params.get(ContractsQueryParams.statusAt));

      filters.startDate = startDate;
      filters.endDate = endDate;
    }

    if (params.has(ContractsQueryParams.owners)) {
      filters.owners = contributorsParam(params.get(ContractsQueryParams.owners));
    }

    if (params.has(ContractsQueryParams.businessUnits)) {
      filters.businessUnits = businessUnitsParam(params.get(ContractsQueryParams.businessUnits));
    }

    return filters;
  }

  static activeFiltersLength(filters: ContractsFilters): number {
    return (
      (filters?.status?.length ? 1 : 0) +
      (filters?.businessUnits?.length ? 1 : 0) +
      (filters?.owners?.length ? 1 : 0) +
      (typeof filters?.archived === 'boolean' ? 1 : 0) +
      (filters?.reminder_status ? 1 : 0) +
      (filters?.tasks?.length ? 1 : 0) +
      (filters?.loss_reasons?.length ? 1 : 0)
    );
  }

  static canSendContractWithoutConfirmation(contract: Contract): boolean {
    if (contract.status !== ContractStatus.draft) {
      return true;
    }

    const prestationsWithContractOffer: Prestation[] = contract.prestations?.filter(
      (prestation: Prestation) => !!prestation.contractOffer
    );
    const allOffersHaveSameTag: boolean = prestationsWithContractOffer?.every(
      (prestation: Prestation) => prestation.contractOffer.tag === prestationsWithContractOffer[0].contractOffer.tag
    );

    return (
      !prestationsWithContractOffer.length ||
      (allOffersHaveSameTag && prestationsWithContractOffer.length === contract.prestations.length)
    );
  }
}
